import { useMemo, useSyncExternalStore } from "react";
import type { ClientScreen } from "../api/types";
import type { PlanId } from "./money";

/**
 * Маленький роутер на history API. Библиотека маршрутизации ради семи адресов —
 * лишняя зависимость; зато разбор и сборка адресов здесь чистые и покрыты тестами.
 *
 * Адреса сайта героя:
 *   /                          страница героя
 *   /listen, /listen/:n        «Плеер»; ?checkout=success&plan=… — вернулись из оплаты
 *                              (RETURN_PATHS.checkoutSuccess), ?gift=thanks — из оплаты подарка
 *   /plans                     пейвол; ?episode=n&plan=yearly&resume=1&checkout=canceled
 *   /join, /signin             регистрация и вход; ?next=/путь — куда вернуться
 *   /account                   аккаунт и подписка
 *   /chat                      «Мессенджер»; ?checkout=success&plan=… — вернулись из оплаты
 *                              (для героя-переписки /listen?checkout=… переходит сюда)
 */

export type Route =
  | { name: "hero" }
  /** plan — что купили: по нему «Плеер» ждёт нужное подтверждение и благодарит за то, что куплено. */
  | {
      name: "listen";
      episode: number | null;
      checkout: "success" | null;
      plan: PlanId | null;
      gift: "thanks" | null;
    }
  | { name: "plans"; episode: number | null; plan: PlanId | null; resume: boolean; canceled: boolean }
  | { name: "join"; next: string | null }
  | { name: "signin"; next: string | null }
  | { name: "account"; portal: boolean }
  /** checkout, plan — как у «Плеера»: вернулись из оплаты, ждём подтверждения в /me. */
  | { name: "chat"; checkout: "success" | null; plan: PlanId | null }
  | { name: "not_found" };

const PLANS: readonly PlanId[] = ["yearly", "monthly", "all_at_once"];

function ordinal(raw: string | null | undefined): number | null {
  if (!raw || !/^\d{1,4}$/.test(raw)) return null;
  const value = Number(raw);
  return value >= 1 ? value : null;
}

/**
 * Куда можно вернуться после входа. Только свои пути: «//evil.com» и «/\evil.com»
 * браузер понимает как другой сайт, и ?next= превратился бы в открытый редирект.
 */
export function safeNext(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const value = raw.trim();
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return null;
  if (/[\u0000-\u001f]/.test(value)) return null;
  return value;
}

export function parseRoute(pathname: string, search: string = ""): Route {
  const params = new URLSearchParams(search);
  const path = pathname.replace(/\/+$/, "") || "/";
  const parts = path.split("/").filter(Boolean);
  const [head, second, ...rest] = parts;

  if (head === undefined) return { name: "hero" };
  if (rest.length > 0) return { name: "not_found" };

  switch (head) {
    case "listen": {
      const episode = second === undefined ? null : ordinal(second);
      if (second !== undefined && episode === null) return { name: "not_found" };
      const checkout = params.get("checkout") === "success" ? "success" : null;
      const plan = checkout ? (PLANS.find((p) => p === params.get("plan")) ?? null) : null;
      const gift = params.get("gift") === "thanks" ? "thanks" : null;
      return { name: "listen", episode, checkout, plan, gift };
    }
    case "plans": {
      if (second !== undefined) return { name: "not_found" };
      const plan = params.get("plan");
      return {
        name: "plans",
        episode: ordinal(params.get("episode")),
        plan: PLANS.find((p) => p === plan) ?? null,
        resume: params.get("resume") === "1",
        canceled: params.get("checkout") === "canceled",
      };
    }
    case "join":
    case "signin":
      if (second !== undefined) return { name: "not_found" };
      return { name: head, next: safeNext(params.get("next")) };
    case "account":
      if (second !== undefined) return { name: "not_found" };
      return { name: "account", portal: params.has("portal") };
    case "chat": {
      if (second !== undefined) return { name: "not_found" };
      const checkout = params.get("checkout") === "success" ? "success" : null;
      const plan = checkout ? (PLANS.find((p) => p === params.get("plan")) ?? null) : null;
      return { name: "chat", checkout, plan };
    }
    default:
      return { name: "not_found" };
  }
}

function withQuery(path: string, query: Record<string, string | null>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null) params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Адрес для маршрута — обратная к parseRoute. */
export function routeHref(route: Route): string {
  switch (route.name) {
    case "hero":
      return "/";
    case "listen":
      return withQuery(route.episode === null ? "/listen" : `/listen/${route.episode}`, {
        checkout: route.checkout,
        plan: route.checkout ? route.plan : null,
        gift: route.gift,
      });
    case "plans":
      return withQuery("/plans", {
        episode: route.episode === null ? null : String(route.episode),
        plan: route.plan,
        resume: route.resume ? "1" : null,
        checkout: route.canceled ? "canceled" : null,
      });
    case "join":
    case "signin":
      return withQuery(`/${route.name}`, { next: route.next });
    case "account":
      return withQuery("/account", { portal: route.portal ? "1" : null });
    case "chat":
      return withQuery("/chat", { checkout: route.checkout, plan: route.checkout ? route.plan : null });
    case "not_found":
      return "/404";
  }
}

/** Сокращения для частых адресов, чтобы экраны не собирали объекты руками. */
export const href = {
  hero: () => "/",
  listen: (episode: number | null = null) =>
    routeHref({ name: "listen", episode, checkout: null, plan: null, gift: null }),
  plans: (options: { episode?: number | null; plan?: PlanId | null; resume?: boolean } = {}) =>
    routeHref({
      name: "plans",
      episode: options.episode ?? null,
      plan: options.plan ?? null,
      resume: options.resume ?? false,
      canceled: false,
    }),
  join: (next: string | null = null) => routeHref({ name: "join", next }),
  signin: (next: string | null = null) => routeHref({ name: "signin", next }),
  account: () => "/account",
  chat: () => "/chat",
};

/**
 * Вернулись из оплаты на адрес «Плеера» (RETURN_PATHS.checkoutSuccess), а герой —
 * переписка: туда же, но в «Мессенджер», с тем же признаком оплаты.
 */
export function chatFromListen(route: Extract<Route, { name: "listen" }>): string {
  return routeHref({ name: "chat", checkout: route.checkout, plan: route.checkout ? route.plan : null });
}

/** Экран для аналитики (ClientEvent.screen); у «не найдено» экрана нет. */
export function screenOf(route: Route): ClientScreen | null {
  switch (route.name) {
    case "hero":
      return "hero";
    case "join":
    case "signin":
      return "auth";
    case "plans":
      return "plans";
    case "listen":
      return "listen";
    case "account":
      return "account";
    case "chat":
      return "chat";
    case "not_found":
      return null;
  }
}

// --- история браузера ------------------------------------------------------------

const NAVIGATE_EVENT = "scrile:navigate";

function snapshot(): string {
  return window.location.pathname + window.location.search;
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener("popstate", onChange);
  window.addEventListener(NAVIGATE_EVENT, onChange);
  return () => {
    window.removeEventListener("popstate", onChange);
    window.removeEventListener(NAVIGATE_EVENT, onChange);
  };
}

/** Адрес внутри сайта: путь с запросом. Внешний (Stripe) — null. */
export function internalPath(url: string, base: string = window.location.href): string | null {
  try {
    const target = new URL(url, base);
    if (target.origin !== new URL(base).origin) return null;
    return target.pathname + target.search + target.hash;
  } catch {
    return null;
  }
}

/**
 * Номер записи в истории внутри сайта. По нему «Назад» понимает, есть ли куда
 * вернуться у нас: человек, открывший пейвол по прямой ссылке, иначе ушёл бы
 * кнопкой «закрыть» с сайта вовсе.
 */
function historyIndex(): number {
  const state: unknown = window.history.state;
  if (typeof state === "object" && state !== null && "idx" in state && typeof state.idx === "number") {
    return state.idx;
  }
  return 0;
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  const path = internalPath(to);
  if (path === null) {
    window.location.assign(to);
    return;
  }
  if (path === snapshot() + window.location.hash) return;
  if (options.replace) window.history.replaceState({ idx: historyIndex() }, "", path);
  else window.history.pushState({ idx: historyIndex() + 1 }, "", path);
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
  // Новый экран начинается сверху; «назад» браузер прокручивает сам
  if (!options.replace) window.scrollTo(0, 0);
}

/**
 * Переход по адресу из ответа сервера (Checkout, Portal): свой адрес — без перезагрузки
 * (в мок-режиме состояние живёт в памяти вкладки), чужой — обычной навигацией.
 */
export function go(url: string): void {
  navigate(url);
}

export function back(fallback: string = "/"): void {
  if (historyIndex() > 0) {
    window.history.back();
    return;
  }
  navigate(fallback, { replace: true });
}

export function useLocationKey(): string {
  return useSyncExternalStore(subscribe, snapshot, () => "/");
}

export function useRoute(): Route {
  const key = useLocationKey();
  return useMemo(() => {
    const url = new URL(key, "http://local");
    return parseRoute(url.pathname, url.search);
  }, [key]);
}
